import { useMemo, useRef, useState, type ReactNode } from 'react'
import isError from 'lodash/isError'
import sumBy from 'lodash/sumBy'
import toInteger from 'lodash/toInteger'
import { Link, useParams } from 'react-router'
import { ArrowLeft, ArrowUpRight, CheckCircle2, XCircle } from 'lucide-react'
import {
  PERMISSION_ACTION_ADD,
  PERMISSION_ACTION_APPROVE,
  PERMISSION_ACTION_EDIT,
  PERMISSION_ACTION_FUND,
  PERMISSION_ENTITY_INVOICE_OFFER,
  PERMISSION_ENTITY_PLATFORM_FEE,
  ROUTE_DISBURSEMENTS,
  ROUTE_ORDER_EVENTS,
  ROUTE_ORDERS,
  ROUTE_PAYMENT,
  ROUTE_RECEIVABLES
} from '@klear/constants'
import { ORDER_EVENT_STATUS_APPROVED, ORDER_EVENT_STATUS_DENIED } from '@klear/constants/order-event-status'
import type {
  FundCheck,
  FundCheckRemedy,
  FundReadinessRow,
  FundReadinessStatus
} from '@klear/invoice-offer/fund-readiness'
import { paymentStatusLabel } from '@klear/payment/presentation'
import { buildDocumentFileUrl } from '@klear/upload/file-url'
import { resolveOrderEventPreviewFileUrl } from '@klear/ui/order-event-file-preview'
import { useOrderEventDocuments } from '@klear/ui/hooks/use-order-event-documents'
import type { OrderEventTimelineItem } from '@klear/order-event/order-event-timeline-tree'
import { timelineItemId } from '@klear/order-event/order-event-timeline-tree'
import { findTimelineEvent } from '@klear/order-event-ui/event-timeline-utils'
import {
  availableActionsFromTimeline,
  canUndoRejectionFromTimeline,
  buildTimelineActionContext
} from '@klear/order-event-ui/event-actions-availability'
import { Button } from '@klear/ui/components/button'
import { Card } from '@klear/ui/components/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@klear/ui/components/dialog'
import { Sheet, SheetContent } from '@klear/ui/components/sheet'
import { Skeleton } from '@klear/ui/components/skeleton'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@klear/ui/components/tooltip'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'
import { useOrderEventTimelineTree } from '@klear/ui/hooks/use-order-events'
import { toast } from '@klear/ui/hooks/use-toast'
import { cn } from '@klear/ui/lib/utils'
import { trpc } from '@klear/ui/lib/trpc'
import {
  DOCUMENT_CHECKS,
  FUND_CHECK_LABELS,
  STATUS_LABELS,
  creditCell,
  daysSince,
  fixLink,
  formatDate,
  formatMoney,
  nextInTurn,
  passText,
  plural
} from '@/app/receivables/fund-checks'
import { StatusPill } from '@/app/receivables/status-pill'
import { FloatingHeader, useScrolledOut, useScrollMainToTop } from '@/app/receivables/floating-header'
import { DOC_SHORT, ReviewSheet, type Reviewing } from '@/app/receivables/review-sheet'
import { DocumentStrip, type StripTile } from '@/app/receivables/document-strip'
import { useRecordReviewChecks } from '@/app/receivables/use-record-review-checks'
import { useReviewView } from '@/app/receivables/use-review-view'
import { isEditableTarget } from '@/app/document-review/use-approval-keyboard'

export default function ReceivablesOffer() {
  const offerId = toInteger(useParams().offerId)
  const { user } = useAuth()
  const [reviewing, setReviewing] = useState<OrderEventTimelineItem | null>(null)
  const [shownReview, setShownReview] = useState<OrderEventTimelineItem | null>(null)
  if (reviewing != null && reviewing !== shownReview) setShownReview(reviewing)
  const [documentId, setDocumentId] = useState<string | null>(null)
  const [view, setView] = useReviewView()
  const [headerRef, collapsed] = useScrolledOut()
  const { data, isLoading, isError, refetch } = trpc.invoiceOffer.fundReadiness.useQuery(
    { offerId },
    { enabled: offerId > 0 }
  )
  useScrollMainToTop(offerId)
  const row = data?.rows[0]
  const { events } = useOrderEventTimelineTree(row?.orderId)
  const actionContext = useMemo(() => buildTimelineActionContext(events), [events])
  useDocumentTitle(row?.offerIdDisplay ?? 'Receivables', row?.traderName ?? undefined)
  const documentRecordIds = useMemo(
    () =>
      (row?.checks ?? []).flatMap((check) =>
        DOCUMENT_CHECKS.includes(check.key) && check.eventId != null ? [check.eventId] : []
      ),
    [row]
  )
  const { documentsFor, isFetching: loadingDocuments } = useOrderEventDocuments(
    documentRecordIds,
    row?.traderId ?? undefined
  )
  const {
    checksOf,
    failed: checksFailed,
    refresh: refreshChecks
  } = useRecordReviewChecks(documentRecordIds, documentsFor, reviewing != null)

  if (isLoading) return <Skeleton className="m-8 h-[300px]" />
  if (isError) {
    return (
      <Card className="m-8 grid gap-3 p-6">
        <p className="text-muted-foreground">Could not load this offer.</p>
        <Button size="sm" variant="outline" className="w-fit" onClick={() => void refetch()}>
          Try again
        </Button>
      </Card>
    )
  }
  if (row == null) {
    return (
      <Card className="m-8 grid gap-3 p-6">
        <p className="text-muted-foreground">Invoice offer not found</p>
        <Link to={`/${ROUTE_RECEIVABLES}`} className="inline-flex w-fit items-center gap-1 hover:underline">
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Receivables
        </Link>
      </Card>
    )
  }

  const status = STATUS_LABELS[row.status]
  const credit = creditCell(row.credit)
  const blocking = row.fundBlockers.length
  const accepted = daysSince(row.acceptedAt)
  const reviewOf = (record: OrderEventTimelineItem): Reviewing => {
    const eventId = timelineItemId(record)
    return {
      record,
      canApprove:
        row.canStillBeFunded &&
        availableActionsFromTimeline(user, actionContext, eventId).some(
          (option) => option.value === PERMISSION_ACTION_APPROVE
        ),
      canEdit: row.canStillBeFunded,
      canUndoRejection: row.canStillBeFunded && canUndoRejectionFromTimeline(user, actionContext, eventId)
    }
  }
  const closeSheet = () => {
    setReviewing(null)
    void refreshChecks()
  }
  const open = (record: OrderEventTimelineItem, fileId: string | null = null) => {
    setDocumentId(fileId)
    setReviewing(record)
  }
  const shownId = shownReview == null ? null : timelineItemId(shownReview)
  const openNextToApprove = () => {
    const next = nextInTurn(documentRecordIds, shownId, (eventId) => {
      const record = findTimelineEvent(events, eventId)
      return record != null && documentsFor(eventId).length > 0 && reviewOf(record).canApprove
    })
    const record = next == null ? undefined : findTimelineEvent(events, next)
    if (record == null) closeSheet()
    else open(record)
  }
  const shownFiles = documentsFor(shownId)
  const shownFileId = shownFiles.find((file) => file.id === documentId)?.id ?? shownFiles[0]?.id
  const tiles = documentRecordIds.flatMap((eventId): StripTile[] => {
    const record = findTimelineEvent(events, eventId)
    const files = documentsFor(eventId)
    if (record == null || (files.length === 0 && loadingDocuments)) return []
    const title = `${DOC_SHORT[record.type] ?? 'Record'} · ${record.envelope.idDisplay}`
    const reviews = checksOf(eventId)?.reviews ?? []
    const status = recordStatus(record.envelope.status)
    if (files.length === 0) {
      return [
        {
          key: `${eventId}`,
          title,
          file: 'No document yet',
          url: null,
          ...status,
          checkCount: sumBy(reviews, (review) => review.checks.length),
          current: eventId === shownId,
          onPick: () => open(record)
        }
      ]
    }
    return files.map((file) => ({
      key: `${eventId}:${file.id}`,
      title,
      file: file.name,
      url: resolveOrderEventPreviewFileUrl(buildDocumentFileUrl(file.documentKey, file.traderId)),
      ...status,
      checkCount: sumBy(
        reviews.filter((review) => review.orderEventDocumentId === file.id),
        (review) => review.checks.length
      ),
      current: eventId === shownId && file.id === shownFileId,
      onPick: () => open(record, file.id)
    }))
  })
  let summary = `${blocking} of ${row.checks.length} checks blocking funding.`
  if (!row.canStillBeFunded) {
    summary =
      blocking === 0 ? 'Every check passes as of today.' : `${blocking} of ${row.checks.length} failing as of today.`
  } else if (blocking === 0) {
    summary = row.readyToFund ? 'Every check passes. This offer can be funded.' : 'Every check passes.'
  }

  return (
    <div className="px-8 pt-8">
      <FloatingHeader visible={collapsed}>
        <Link
          to={`/${ROUTE_RECEIVABLES}`}
          className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Receivables
        </Link>
        <span className="text-muted-foreground" aria-hidden>
          /
        </span>
        <h2 className="font-semibold">{row.offerIdDisplay}</h2>
        <StatusPill tone={status.tone}>{status.label}</StatusPill>
      </FloatingHeader>

      <div className="grid gap-8 pb-6">
        <Link
          to={`/${ROUTE_RECEIVABLES}`}
          className="inline-flex w-fit items-center gap-1 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Receivables
        </Link>

        <div className="flex flex-wrap items-start gap-x-8 gap-y-4">
          <div ref={headerRef} className="flex items-center gap-3">
            <h2 className="text-2xl font-bold tracking-tight">{row.offerIdDisplay}</h2>
            <StatusPill tone={status.tone}>{status.label}</StatusPill>
          </div>
          <HeaderField label="Trader" value={row.traderName} />
          <HeaderField label="Customer" value={row.customerName} />
          <HeaderField
            label="Order"
            value={row.orderIdDisplay}
            to={row.orderIdDisplay == null ? undefined : `/${ROUTE_ORDERS}/${row.orderIdDisplay}`}
          />
          <HeaderField
            label="Invoice"
            value={row.invoiceIdDisplay ?? 'No invoice yet'}
            to={row.invoiceIdDisplay == null ? undefined : `/${ROUTE_ORDER_EVENTS}/${row.invoiceIdDisplay}`}
          />
          <HeaderField label="Credit" value={credit.label} detail={credit.detail} />
          <HeaderField label="SPV" value={row.fundingSpv} />
          <HeaderField
            label="Purchase price"
            value={formatMoney(row.purchasePrice)}
            detail={row.priceIsEstimate ? 'Estimate, repriced until funded' : undefined}
          />
          <HeaderField label="Fees" value={formatMoney(row.feeTotal)} />
          <HeaderField label="Due" value={formatDate(row.dueDate)} />
          {row.fundedAt != null ? (
            <HeaderField label="Funded" value={formatDate(row.fundedAt)} />
          ) : (
            <HeaderField label="Accepted" value={accepted ?? 'Not yet'} />
          )}
        </div>

        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="text-xl font-semibold">Fund checks</h3>
          <p className="text-muted-foreground">{summary}</p>
        </div>
      </div>

      <div className="grid gap-2">
        {row.checks.map((check) => {
          const record = check.eventId == null ? undefined : findTimelineEvent(events, check.eventId)
          return (
            <CheckRow
              key={check.key}
              row={row}
              check={check}
              review={record == null ? undefined : reviewOf(record)}
              onReview={open}
            />
          )
        })}
      </div>
      <FundDock row={row} />

      <Sheet open={reviewing != null} onOpenChange={(isOpen) => (isOpen ? undefined : closeSheet())}>
        <SheetContent
          onEscapeKeyDown={(event) => {
            if (window.document.fullscreenElement == null) return
            event.preventDefault()
            void window.document.exitFullscreen()
          }}
          onKeyDown={(event) => {
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
            if (event.altKey || event.metaKey || event.ctrlKey || event.shiftKey) return
            if (!(event.target instanceof Node) || !event.currentTarget.contains(event.target)) return
            if (isEditableTarget(event.target)) return
            const tile = tiles[tiles.findIndex((one) => one.current) + (event.key === 'ArrowRight' ? 1 : -1)]
            if (tile == null) return
            event.preventDefault()
            tile.onPick()
          }}
          side="right"
          className="grid w-screen grid-cols-[minmax(0,1fr)_auto] grid-rows-[auto_minmax(0,1fr)_auto] gap-x-0 gap-y-4 p-6 sm:max-w-none"
        >
          {shownReview != null ? (
            <>
              <ReviewSheet
                key={timelineItemId(shownReview)}
                row={row}
                reviewing={reviewOf(findTimelineEvent(events, timelineItemId(shownReview)) ?? shownReview)}
                documents={shownFiles}
                loadingDocuments={loadingDocuments}
                documentId={documentId}
                checks={checksOf(shownId)}
                checksFailed={checksFailed}
                view={view}
                onViewChange={setView}
                onClose={closeSheet}
                onApproved={openNextToApprove}
              />
              <div className="col-start-1 row-start-3 min-w-0 border-t pr-4 pt-3">
                <DocumentStrip
                  label={`Documents on ${row.offerIdDisplay}`}
                  tiles={tiles}
                  showCounts={view === 'review'}
                />
              </div>
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  )
}

function HeaderField({
  label,
  value,
  detail,
  to
}: {
  label: string
  value: string | null
  detail?: string
  to?: string
}) {
  return (
    <div className="grid min-w-0 gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      {to == null ? (
        <b className="font-medium">{value ?? '—'}</b>
      ) : (
        <Link to={to} className="font-medium underline-offset-4 hover:underline">
          {value}
        </Link>
      )}
      {detail ? <small className="text-xs text-muted-foreground">{detail}</small> : null}
    </div>
  )
}

function CheckRow({
  row,
  check,
  review,
  onReview
}: {
  row: FundReadinessRow
  check: FundCheck
  review: Reviewing | undefined
  onReview: (record: OrderEventTimelineItem) => void
}) {
  const record = review?.record
  const passed = check.reason == null
  const isDocument = DOCUMENT_CHECKS.includes(check.key)
  const defaultFix = check.remedy == null ? undefined : DEFAULT_FIXES[check.remedy]
  const fix = fixLink(row, check)
  let text = check.reason ?? passText(row, check)
  if (passed && isDocument && record != null) text = `${record.envelope.idDisplay} approved`

  return (
    <div className="grid gap-3 rounded-lg border px-4 py-3">
      <div className="flex min-h-9 flex-wrap items-center gap-3">
        {passed ? (
          <CheckCircle2 className="h-5 w-5 shrink-0 text-success" role="img" aria-label="Passing" />
        ) : (
          <XCircle className="h-5 w-5 shrink-0 text-danger" role="img" aria-label="Failing" />
        )}
        <span className="w-40 font-medium">{FUND_CHECK_LABELS[check.key]}</span>
        <span className={cn('min-w-0 flex-1 basis-60', passed && 'text-muted-foreground')}>{text}</span>
        <div className="flex flex-wrap gap-2">
          {isDocument && record != null && review?.canApprove ? (
            <Button size="sm" onClick={() => onReview(record)}>
              Review
            </Button>
          ) : null}
          {isDocument &&
          record != null &&
          !review?.canApprove &&
          (record.envelope.status === ORDER_EVENT_STATUS_APPROVED ||
            record.envelope.status === ORDER_EVENT_STATUS_DENIED) ? (
            <Button size="sm" variant="outline" onClick={() => onReview(record)}>
              {review?.canEdit ? 'Edit' : 'View'}
            </Button>
          ) : null}
          {defaultFix != null ? <CreationDefaultFix row={row} which={defaultFix} /> : null}
          {fix ? (
            <Button size="sm" variant="outline" asChild>
              <Link to={fix.to}>
                {fix.label}
                <ArrowUpRight className="ml-1 h-3.5 w-3.5" aria-hidden />
              </Link>
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function recordStatus(status: string): Pick<StripTile, 'status' | 'tone'> {
  if (status === ORDER_EVENT_STATUS_APPROVED) return { status: 'Approved', tone: 'ok' }
  if (status === ORDER_EVENT_STATUS_DENIED) return { status: 'Rejected', tone: 'bad' }
  return { status: 'Awaiting approval', tone: 'warn' }
}

const DEFAULT_FIXES: Partial<Record<FundCheckRemedy, 'platformFee' | 'paymentDetails'>> = {
  applyDefaultPlatformFee: 'platformFee',
  applyDefaultPaymentDetails: 'paymentDetails'
}

const CREATION_DEFAULTS = {
  platformFee: {
    button: 'Price platform fee',
    done: 'Platform fee added',
    failed: 'Platform fee not added',
    detail: "priced from the funder's pricing."
  },
  paymentDetails: {
    button: 'Use default payment details',
    done: 'Payment details set',
    failed: 'Payment details not set',
    detail: 'uses the default network and accounts.'
  }
}

function CreationDefaultFix({ row, which }: { row: FundReadinessRow; which: 'platformFee' | 'paymentDetails' }) {
  const { user } = useAuth()
  const [done, setDone] = useState(false)
  const running = useRef(false)
  const addFee = trpc.invoiceOffer.addDefaultPlatformFee.useMutation()
  const setPayment = trpc.invoiceOffer.applyDefaultPaymentDetails.useMutation()
  const mutation = which === 'platformFee' ? addFee : setPayment
  const utils = trpc.useUtils()
  const copy = CREATION_DEFAULTS[which]
  const allowed =
    user.hasPermission(PERMISSION_ENTITY_INVOICE_OFFER, PERMISSION_ACTION_EDIT) &&
    (which !== 'platformFee' || user.hasPermission(PERMISSION_ENTITY_PLATFORM_FEE, PERMISSION_ACTION_ADD))
  if (!allowed) return null
  const run = () => {
    if (running.current) return
    running.current = true
    void mutation
      .mutateAsync({ invoiceOfferId: row.offerId })
      .then(async () => {
        setDone(true)
        toast({ title: copy.done, description: `${row.offerIdDisplay} ${copy.detail}` })
        await utils.invoiceOffer.fundReadiness.invalidate()
      })
      .catch((error: unknown) =>
        toast({
          title: copy.failed,
          description: isError(error) ? error.message : 'Try again.',
          variant: 'destructive'
        })
      )
      .finally(() => {
        running.current = false
      })
  }
  return (
    <Button size="sm" variant="outline" disabled={mutation.isPending || done} onClick={run}>
      {copy.button}
    </Button>
  )
}

const DOCK_PANELS: Record<FundReadinessStatus, 'fund' | 'payment' | 'quote' | 'recovery' | 'closed'> = {
  ready: 'fund',
  blocked: 'fund',
  funding: 'payment',
  fundingFailed: 'payment',
  quote: 'quote',
  overdue: 'recovery',
  repaying: 'recovery',
  funded: 'recovery',
  completed: 'closed',
  voided: 'closed'
}

function FundDock({ row }: { row: FundReadinessRow }) {
  const { user } = useAuth()
  const [confirming, setConfirming] = useState(false)
  const [sent, setSent] = useState(false)
  const utils = trpc.useUtils()
  const fund = trpc.disbursement.disburseAdvances.useMutation()
  const hasFundPermission = user.hasPermission(PERMISSION_ENTITY_INVOICE_OFFER, PERMISSION_ACTION_FUND)
  const trader = row.traderName ?? 'the trader'
  const price = formatMoney(row.purchasePrice)
  const { label } = STATUS_LABELS[row.status]

  let title = row.readyToFund ? 'Ready to fund' : `${plural(row.fundBlockers.length, 'check')} blocking funding`
  let detail = [`${price} to ${trader}`, row.fundingSpv].filter(Boolean).join(' · ')
  let side: ReactNode = null
  let tone = row.status === 'ready' ? 'border-success' : ''
  const payment = row.disbursement
  const panel = DOCK_PANELS[row.status]

  if (panel === 'closed') {
    title = `${label} · ${price} to ${trader}`
    detail =
      row.status === 'voided'
        ? 'This offer was voided and cannot be funded unless it is reset.'
        : [
            row.fundedAt == null ? null : `Funded ${formatDate(row.fundedAt)}`,
            row.repaidAt == null ? null : `Completed ${formatDate(row.repaidAt)}`,
            row.fundingSpv
          ]
            .filter(Boolean)
            .join(' · ')
  } else if (panel === 'payment' && payment != null) {
    const failed = row.status === 'fundingFailed'
    title = `${payment.paymentIdDisplay ?? 'Advance payment'} · ${paymentStatusLabel(payment.status)}`
    const outcome = failed
      ? `${price} to ${trader} did not go out: ${payment.error ?? paymentStatusLabel(payment.status)}.`
      : `${price} to ${trader}.`
    detail = [outcome, row.advancePaymentNextStep].filter(Boolean).join(' ')
    side = (
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" asChild>
          <Link to={`/${ROUTE_PAYMENT}/${payment.paymentId}`}>
            {payment.paymentIdDisplay ?? 'Payment'}
            <ArrowUpRight className="ml-1 h-3.5 w-3.5" aria-hidden />
          </Link>
        </Button>
        {failed ? (
          <Button size="sm" variant="outline" asChild>
            <Link to={`/${ROUTE_DISBURSEMENTS}`}>
              Disbursements
              <ArrowUpRight className="ml-1 h-3.5 w-3.5" aria-hidden />
            </Link>
          </Button>
        ) : null}
      </div>
    )
    tone = failed ? 'border-danger' : 'border-info'
  } else if (panel === 'quote') {
    title = 'Awaiting acceptance'
    detail = `${plural(row.fundBlockers.length, 'check')} blocking funding`
    side = <Figure value={price} label={row.priceIsEstimate ? 'Purchase price, estimated' : 'Purchase price'} />
  } else if (panel === 'recovery') {
    const recovery = row.recovery
    title = `${label} · ${price} to ${trader}`
    detail = [
      `Funded ${formatDate(row.fundedAt)}`,
      row.fundingSpv,
      recovery?.extensionFees ? `${formatMoney(recovery.extensionFees)} late fees unpaid` : 'No late fees'
    ]
      .filter(Boolean)
      .join(' · ')
    side = <Figure value={formatDate(recovery?.dueDate)} label="Recovery due" warn={row.status === 'overdue'} />
    tone = row.status === 'overdue' ? 'border-warning' : ''
  } else {
    let blocked: string | null = null
    if (!hasFundPermission) blocked = 'You need permission to fund invoice offers.'
    else if (!row.readyToFund) blocked = 'Every fund check must pass first.'
    if (!hasFundPermission && row.readyToFund) detail = `${detail} · Funding needs someone with fund permission`
    const button = (
      <Button
        disabled={blocked != null || fund.isPending || sent}
        className={cn('h-10 px-6 text-sm', blocked != null && 'pointer-events-none')}
        onClick={() => setConfirming(true)}
      >
        Fund
      </Button>
    )
    side =
      blocked == null ? (
        button
      ) : (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0} className="inline-block">
                {button}
              </span>
            </TooltipTrigger>
            <TooltipContent>{blocked}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )
  }

  const confirm = async () => {
    await fund
      .mutateAsync({ disbursableIds: [row.offerId] })
      .then(async () => {
        setSent(true)
        toast({ title: `${row.offerIdDisplay} funding scheduled`, description: `${price} to ${trader}.` })
        setConfirming(false)
        await utils.invoiceOffer.fundReadiness.invalidate()
      })
      .catch((error: unknown) => {
        toast({
          title: 'Funding failed',
          description: isError(error) ? error.message : 'Try again.',
          variant: 'destructive'
        })
      })
  }

  return (
    <>
      <div className="pointer-events-none sticky bottom-[var(--agent-console-height,0px)] z-[3] -mx-8 -mt-4 bg-[linear-gradient(to_bottom,transparent,var(--background)_55%)] px-8 pb-6 pt-16">
        <div
          role="region"
          aria-label="Offer status"
          className={cn(
            'pointer-events-auto flex flex-wrap items-center justify-between gap-x-6 gap-y-3 rounded-xl border bg-background px-5 py-4 shadow-[0_8px_24px_rgba(0,0,0,0.18)]',
            tone
          )}
        >
          <div className="grid min-w-0 gap-0.5">
            <b className="font-semibold">{title}</b>
            <span className="text-muted-foreground">{detail}</span>
          </div>
          {side}
        </div>
      </div>
      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent className="max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Fund {row.offerIdDisplay}?</DialogTitle>
            <DialogDescription>
              Klear sends the purchase price now. The offer is marked Funded once the payment goes out.
            </DialogDescription>
          </DialogHeader>
          <dl className="grid gap-2">
            {[
              ['Trader', row.traderName ?? '—'],
              ['Customer', row.customerName ?? '—'],
              ['Purchase price', price],
              ['SPV', row.fundingSpv ?? '—']
            ].map(([field, value]) => (
              <div key={field} className="flex justify-between gap-4">
                <dt className="text-muted-foreground">{field}</dt>
                <dd className="text-right font-medium tabular-nums">{value}</dd>
              </div>
            ))}
          </dl>
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button size="sm" disabled={fund.isPending} onClick={() => void confirm()}>
              Fund offer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function Figure({ value, label, warn }: { value: string; label: string; warn?: boolean }) {
  return (
    <div className="grid gap-0.5 text-right">
      <b className={cn('text-lg font-semibold tabular-nums', warn && 'text-warning')}>{value}</b>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  )
}
